import { toNumber } from 'lodash';
import { COLUMN_TYPES, DEFAULT_FIRM_SUMMARY_COLUMNS, SpreadsheetDefinition } from 'common/types/summaryDefinitions';
import {
  FIRM_SUMMARY_DRAFT_TITLE,
  FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE,
  FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
} from 'pages/Firms/common/constants';
import { FundByCompany } from 'pages/Firms/hooks/useFirmSummaryTab';
import { generateColumnKey, getArrayValue, getObjectValue, shortDate } from 'utilities';
import { CreateColumnsParams, FirmSummaryColumn } from './createColumns.types';

/**
 * Column generator functions by column type
 * Each function creates a FirmSummaryColumn for that column type
 */
type ColumnGenerator = (fundsByCompany: FundByCompany[]) => FirmSummaryColumn;

const toOptionalNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** True if any child row (same company, is_parent false) is draft (!is_published && !is_final). */
const hasDraftChildren = (fundsByCompany: FundByCompany[], companyName: string): boolean =>
  getArrayValue(fundsByCompany).some(item => {
    const { company_name: c, is_parent: ip, is_published: pub, is_final: fin } = getObjectValue(item);
    return c === companyName && !ip && !pub && !fin;
  });

const formatBreakevenExitEquityValue = (minValue: number | null, maxValue: number | null): string | null => {
  const hasMin = typeof minValue === 'number' && Number.isFinite(minValue);
  const hasMax = typeof maxValue === 'number' && Number.isFinite(maxValue);

  if (!hasMin && !hasMax) {
    return null;
  }

  const formatNumber = (value: number) => value.toLocaleString('en-US', { maximumFractionDigits: 0 });

  if (hasMin && hasMax && minValue !== maxValue) {
    return `${formatNumber(minValue)} - ${formatNumber(maxValue)}`;
  }

  const valueToFormat = hasMax ? maxValue : minValue;
  return valueToFormat == null ? null : formatNumber(valueToFormat);
};

const columnGenerators: Record<string, ColumnGenerator> = {
  [FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, company_slug: companySlug, fund_name: fundName } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: {
          companyName,
          companySlug,
          fundName,
          value: null,
        },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.FUND_NAME]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return { ...accumulator, [columnKey]: { value: fundName } };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.VALUATION_DATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, measurement_date: fundValuationDate } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return { ...accumulator, [columnKey]: { value: shortDate(fundValuationDate) } };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.INITIAL_INVESTMENT_DATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, initial_investment_date: fundInitialInvestmentDate } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return { ...accumulator, [columnKey]: { value: shortDate(fundInitialInvestmentDate) } };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.MOST_RECENT_INVESTMENT_DATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        most_recent_investment_date: fundMostRecentInvestmentDate,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return { ...accumulator, [columnKey]: { value: shortDate(fundMostRecentInvestmentDate) } };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.DILUTED_OWNERSHIP]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        diluted_ownership: fundFullyDilutedOwnershipPercentage,
        fund_name: fundName,
        funds_count: fundsCount,
        is_parent: isParent,
        parent_type: parentType,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      const value =
        isParent && parentType === 'fund' ? null : toOptionalNumber(fundFullyDilutedOwnershipPercentage);
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.OUTSTANDING_OWNERSHIP]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        outstanding_ownership: fundOutstandingOwnershipPercentage,
        fund_name: fundName,
        funds_count: fundsCount,
        is_parent: isParent,
        parent_type: parentType,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      const value =
        isParent && parentType === 'fund' ? null : toOptionalNumber(fundOutstandingOwnershipPercentage);
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.INVESTED_CAPITAL]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_cost_basis: fundinvestedCapital,
        fund_name: fundName,
        funds_count: fundsCount,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value: toNumber(fundinvestedCapital) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.REALIZED_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
        realized_value: fundRealizedValue,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, isDraft, value: toOptionalNumber(fundRealizedValue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.UNREALIZED_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
        unrealized_value: fundUnrealizedValue,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, isDraft, value: toNumber(fundUnrealizedValue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.CURRENT_FUND_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        current_fund_value: currentFundValue,
        fund_name: fundName,
        funds_count: fundsCount,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, isDraft, value: toNumber(currentFundValue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.TOTAL_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
        total_value: fundTotalValue,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: {
          draftValue: FIRM_SUMMARY_DRAFT_TITLE,
          funds_count: fundsCount,
          isDraft,
          value: toNumber(fundTotalValue),
        },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.BREAKEVEN_EXIT_EQUITY_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        breakeven_exit_equity_value_max: breakevenMax,
        breakeven_exit_equity_value_min: breakevenMin,
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      const formattedValue = formatBreakevenExitEquityValue(
        toOptionalNumber(breakevenMin),
        toOptionalNumber(breakevenMax)
      );
      return {
        ...accumulator,
        [columnKey]: {
          funds_count: fundsCount,
          isDraft,
          value: formattedValue,
        },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.EQUITY_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        equity_value: equityValue,
        fund_name: fundName,
        funds_count: fundsCount,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, isDraft, value: toNumber(equityValue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.ENTERPRISE_VALUE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        enterprise_value: enterpriseValue,
        fund_name: fundName,
        funds_count: fundsCount,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, isDraft, value: toNumber(enterpriseValue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.ENTERPRISE_VALUATION_METHOD]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        enterprise_valuation_method: enterpriseValuationMethod,
        fund_name: fundName,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: enterpriseValuationMethod },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.VALUATION_APPROACH]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, valuation_approach: valuationApproach } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: valuationApproach },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.ALLOCATION_METHOD]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { allocation_method: allocationMethod, company_name: companyName, fund_name: fundName } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: allocationMethod },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.DCF_DISCOUNT_RATE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, dcf_discount_rate: discountRate } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: toOptionalNumber(discountRate) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_1]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, projected_revenue_year_1: projectedRevenue } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: toOptionalNumber(projectedRevenue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_2]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, projected_revenue_year_2: projectedRevenue } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: toOptionalNumber(projectedRevenue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PROJECTED_REVENUE_YEAR_3]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, projected_revenue_year_3: projectedRevenue } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: toOptionalNumber(projectedRevenue) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.VALUATION_STATUS]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const { company_name: companyName, fund_name: fundName, valuation_status: valuationStatus } =
        getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: valuationStatus },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.PERCENT_CHANGE_FROM_PREVIOUS]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        percent_change_from_previous: percentChange,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value: toNumber(percentChange) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.TOTAL_INSIGHT_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        total_insight_preference: totalInsightPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value: toNumber(totalInsightPreference) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.FUND_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        fund_preference: fundPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value: toNumber(fundPreference) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.SPECIFIC_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        specific_preference: specificPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value: toNumber(specificPreference) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.TOTAL_COMPANY_PREFERENCE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        funds_count: fundsCount,
        total_company_preference: totalCompanyPreference,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { funds_count: fundsCount, value: toNumber(totalCompanyPreference) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.IMPLIED_ARR_MULTIPLE]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        implied_arr_multiple: impliedArrMultiple,
      } = getObjectValue(current);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { value: toOptionalNumber(impliedArrMultiple) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.GROSS_IRR]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_name: fundName,
        gross_irr: fundGrossIRR,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { isDraft, value: toNumber(fundGrossIRR) },
      };
    }, {} as FirmSummaryColumn),

  [COLUMN_TYPES.MOIC]: fundsByCompany =>
    getArrayValue(fundsByCompany).reduce((accumulator, current) => {
      const {
        company_name: companyName,
        fund_cost_basis: fundinvestedCapital,
        fund_name: fundName,
        is_final: isFinal,
        is_parent: isParent,
        is_published: isPublished,
        moic,
        total_value: fundTotalValue,
      } = getObjectValue(current);
      const isDraft = isParent ? hasDraftChildren(fundsByCompany, companyName) : (!isPublished && !isFinal);
      const fundMOIC =
        toOptionalNumber(moic) ??
        (toNumber(fundinvestedCapital) > 0 ? toNumber(fundTotalValue) / toNumber(fundinvestedCapital) : 0);
      const columnKey = generateColumnKey({
        id: companyName,
        name: fundName,
        prefix: FIRM_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX,
      });
      return {
        ...accumulator,
        [columnKey]: { isDraft, value: toNumber(fundMOIC) },
      };
    }, {} as FirmSummaryColumn),
};

/**
 * Column types that have generators and can be rendered in Firm Summary.
 * Used to differentiate supported vs unsupported columns in the Add column dropdown.
 */
export const SUPPORTED_FIRM_SUMMARY_COLUMN_TYPES = new Set(Object.keys(columnGenerators));

export const getRenderedFirmSummaryColumnTypes = (
  columnTypes: string[],
  isProcessManagementEnabled?: boolean
): string[] => {
  const columnTypesWithoutProcessManagement = columnTypes.filter(
    columnType => columnType !== FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE
  );

  return isProcessManagementEnabled
    ? [FIRM_SUMMARY_PROCESS_MANAGEMENT_COLUMN_TYPE, ...columnTypesWithoutProcessManagement]
    : columnTypesWithoutProcessManagement;
};

/**
 * Get ordered column types from a spreadsheet definition
 */
export function getOrderedColumnTypes(definition?: SpreadsheetDefinition): string[] {
  if (!definition?.columns) {
    // Fall back to default columns (excluding company_name as it's handled separately by rows)
    return DEFAULT_FIRM_SUMMARY_COLUMNS.filter(col => col !== COLUMN_TYPES.COMPANY_NAME);
  }

  // Sort by column index and extract column types, excluding company_name
  return Object.entries(definition.columns)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, colType]) => colType)
    .filter(col => col !== COLUMN_TYPES.COMPANY_NAME);
}

const createColumns = (params: CreateColumnsParams & { orderedColumnTypes?: string[] }): FirmSummaryColumn[] => {
  const { fundsByCompany, spreadsheetDefinition, orderedColumnTypes } = params;

  // Get ordered column types from definition or use defaults
  const columnTypes = orderedColumnTypes ?? getOrderedColumnTypes(spreadsheetDefinition);

  // Build columns dynamically based on the ordered column types
  const columns: FirmSummaryColumn[] = [];

  columnTypes.forEach(columnType => {
    const generator = columnGenerators[columnType];
    if (generator) {
      columns.push(generator(fundsByCompany));
    }
  });

  return columns;
};

export default createColumns;
